import React, { useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { useColors } from 'contexts/colors';
import useTranslations from 'hooks/use-translation';

import { GreyCapsText, Grid, Link } from 'components/core';
import ChevronIcon from 'assets/chevron-icon';
import { CategoryLink } from 'shop/hooks/categories/use-category-links';
import ROUTES from 'src/routes';
import { Clickable } from 'shared/components';
import { ShopCategories } from './shop-categories';

type ShopByOptionProps = {
  copy: string;
  href?: string;
  optionKey: number;
  value: string;
};

type ShopMenuProps = {
  categories: CategoryLink[];
};

export const ShopMenu = ({ categories }: ShopMenuProps): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState(0);
  const colors = useColors();
  const { t } = useTranslations();
  const { DISPENSARIES } = ROUTES;
  const SHOP_OPTIONS = [
    {
      value: 'categories',
      copy: t('common.categories', 'Categories'),
      hasDetail: true,
      detailName: t('common.category', 'Category'),
    },
    {
      value: 'dispensaries',
      copy: t('common.dispensaries', 'Dispensaries'),
      href: DISPENSARIES,
    },
  ];

  const DetailedShopByOption = ({ copy, optionKey, value }: Omit<ShopByOptionProps, 'href'>): JSX.Element => (
    <ShopByOption key={value} onClick={() => setSelectedOption(optionKey)} selected={selectedOption === optionKey}>
      {copy}
      <ChevronIconRight fill={colors.grey[60]} width={12} />
    </ShopByOption>
  );

  const LinkShopByOption = ({ copy, href, value }: Omit<ShopByOptionProps, 'optionKey'>): JSX.Element => (
    <Link key={value} href={href}>
      <ShopByOption>{copy}</ShopByOption>
    </Link>
  );

  return (
    <Wrapper>
      <StyledGrid container>
        <ShopByContainer item xs={12} sm={4} md={3}>
          <GreyCapsText fontSize={14} pl='52px' mb='4px'>
            {t('common.shop', 'Shop')}
          </GreyCapsText>
          {SHOP_OPTIONS.map(({ value, copy, hasDetail = false, href }, key) =>
            hasDetail ? (
              <DetailedShopByOption copy={copy} optionKey={key} value={value} />
            ) : (
              <LinkShopByOption copy={copy} href={href} value={value} />
            )
          )}
        </ShopByContainer>
        {_.isFinite(selectedOption) && SHOP_OPTIONS[selectedOption].hasDetail && (
          <DetailContainer item xs={12} sm={8} md={9}>
            <GreyCapsText fontSize={14} pl='12px' mb='4px'>
              {t('global-navigation.shopBy', {
                defaultValue: 'Shop by {{detail}}',
                detail: SHOP_OPTIONS[selectedOption].detailName,
              })}
            </GreyCapsText>
            {SHOP_OPTIONS[selectedOption].value === 'categories' && <ShopCategories categories={categories} />}
          </DetailContainer>
        )}
      </StyledGrid>
    </Wrapper>
  );
};

type Theme = {
  theme: {
    colors: {
      blueGrey: {
        95: string;
      };
    };
  };
};

const StyledGrid = styled(Grid)`
  height: 100%;
  span {
    height: 25px;
  }
`;

const ShopByContainer = styled(Grid)`
  border-right: ${({ theme }: Theme) => `2px solid ${theme.colors.blueGrey[95]}`};
  display: flex;
  flex-direction: column;
`;

const ShopByOption = styled(Clickable)`
  align-items: center;
  background-color: ${({ selected, theme }) => (selected ? theme.colors.primaryGrey : theme.colors.white)};
  color: ${({ theme }) => theme.colors.grey[25]};
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: bold;
  height: 63px;
  justify-content: space-between;
  padding-left: 52px;
  padding-right: 27px;
  width: 100%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[95]};
  }
`;

const ChevronIconRight = styled(ChevronIcon)`
  transform: rotate(-90deg);
`;

const DetailContainer = styled(Grid)`
  display: flex;
  flex-direction: column;
  padding-left: 40px;
`;

const Wrapper = styled.div`
  box-shadow: inset 0 2.5px 2px rgb(0 0 0 / 6%);
  padding: 34px 0;
`;
